/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */

import { css, jsx, keyframes } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { Arc, Cross, Sparkle, Square } from './glyphs';

// Animation parameters - adjust these to control timing
const ANIMATION_DURATION = 0.5; // Duration of each animation in seconds
const ANIMATION_GAP = 0.5; // Gap between animations in seconds
const ANIMATION_SPEED_MULTIPLIER = 2; // Speed multiplier (higher = faster)
const FORWARD_REVERSE_GAP = 0.5; // Gap between forward and reverse sequences (in seconds)
const CYCLE_END_PAUSE = 1.5; // Pause at the end of the cycle before restarting (in seconds)

/**
 * Animation timings organized by sequence
 * Forward: Sparkle → Square → Arc → Cross
 * Reverse: Cross → Arc → Square → Sparkle
 *
 * Computed as constants for static evaluation by Compiled
 */
// Forward sequence timings (in seconds)
const SPARKLE_START = 0;
const SPARKLE_END = SPARKLE_START + ANIMATION_DURATION;
const SQUARE_START = SPARKLE_END + ANIMATION_GAP;
const SQUARE_END = SQUARE_START + ANIMATION_DURATION;
const ARC_START = SQUARE_END + ANIMATION_GAP;
const ARC_END = ARC_START + ANIMATION_DURATION;
const CROSS_START = ARC_END + ANIMATION_GAP;
const CROSS_END = CROSS_START + ANIMATION_DURATION;
const FORWARD_END = CROSS_END + ANIMATION_GAP;

// Reverse sequence timings (starts after forward ends + gap)
const CROSS_REVERSE_START = FORWARD_END + FORWARD_REVERSE_GAP;
const CROSS_REVERSE_END = CROSS_REVERSE_START + ANIMATION_DURATION;
const ARC_REVERSE_START = CROSS_REVERSE_END + ANIMATION_GAP;
const ARC_REVERSE_END = ARC_REVERSE_START + ANIMATION_DURATION;
const SQUARE_REVERSE_START = ARC_REVERSE_END + ANIMATION_GAP;
const SQUARE_REVERSE_END = SQUARE_REVERSE_START + ANIMATION_DURATION;
const SPARKLE_REVERSE_START = SQUARE_REVERSE_END + ANIMATION_GAP;
const SPARKLE_REVERSE_END = SPARKLE_REVERSE_START + ANIMATION_DURATION;

// Calculate total cycle time: from start to end of sparkle reverse animation + pause
const TOTAL_CYCLE_TIME_SECONDS = SPARKLE_REVERSE_END + CYCLE_END_PAUSE;
const TOTAL_CYCLE_TIME = TOTAL_CYCLE_TIME_SECONDS / ANIMATION_SPEED_MULTIPLIER;

// Generate percentage constants (as static string literals for Compiled)
// Use TOTAL_CYCLE_TIME_SECONDS (before speed multiplier) for percentage calculations
// so that percentages stay within 0-100%
const SPARKLE_END_PERCENT = String((SPARKLE_END / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const SPARKLE_REVERSE_START_PERCENT =
	String((SPARKLE_REVERSE_START / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const SPARKLE_REVERSE_END_PERCENT =
	String((SPARKLE_REVERSE_END / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const SQUARE_START_PERCENT = String((SQUARE_START / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const SQUARE_END_PERCENT = String((SQUARE_END / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const SQUARE_REVERSE_START_PERCENT =
	String((SQUARE_REVERSE_START / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const SQUARE_REVERSE_END_PERCENT =
	String((SQUARE_REVERSE_END / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const ARC_START_PERCENT = String((ARC_START / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const ARC_END_PERCENT = String((ARC_END / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const ARC_REVERSE_START_PERCENT =
	String((ARC_REVERSE_START / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const ARC_REVERSE_END_PERCENT = String((ARC_REVERSE_END / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const CROSS_START_PERCENT = String((CROSS_START / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const CROSS_END_PERCENT = String((CROSS_END / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const CROSS_REVERSE_START_PERCENT =
	String((CROSS_REVERSE_START / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';
const CROSS_REVERSE_END_PERCENT =
	String((CROSS_REVERSE_END / TOTAL_CYCLE_TIME_SECONDS) * 100) + '%';

/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values -- Computed percentages for parameterized animations */

// 1. Sparkle grows forward then reverses
// Structure: hold scale(0) -> animate to scale(1) -> hold scale(1) -> animate back to scale(0) -> hold scale(0)
const sparkleGrowKeyframes = keyframes({
	'0%': {
		transform: 'scale(0)',
	},
	[SPARKLE_END_PERCENT]: {
		transform: 'scale(1)',
	},
	[SPARKLE_REVERSE_START_PERCENT]: {
		transform: 'scale(1)',
	},
	[SPARKLE_REVERSE_END_PERCENT]: {
		transform: 'scale(0)',
	},
	'100%': {
		transform: 'scale(0)',
	},
});

// 2. Square circle grows forward then reverses (like sparkle)
// Structure: hold scale(0) -> animate to scale(1) -> hold scale(1) -> animate back to scale(0) -> hold scale(0)
const squareGrowKeyframes = keyframes({
	'0%': {
		transform: 'scale(0)',
	},
	[SQUARE_START_PERCENT]: {
		transform: 'scale(0)',
	},
	[SQUARE_END_PERCENT]: {
		transform: 'scale(1)',
	},
	[SQUARE_REVERSE_START_PERCENT]: {
		transform: 'scale(1)',
	},
	[SQUARE_REVERSE_END_PERCENT]: {
		transform: 'scale(0)',
	},
	'100%': {
		transform: 'scale(0)',
	},
});

// 3. Arc stroke width grows forward then reverses
// Structure: hold 0 -> animate to 133.5 -> hold 133.5 -> animate back to 0 -> hold 0
const arcStrokeKeyframes = keyframes({
	'0%': {
		strokeWidth: 0,
	},
	[ARC_START_PERCENT]: {
		strokeWidth: 0,
	},
	[ARC_END_PERCENT]: {
		strokeWidth: 133.5,
	},
	[ARC_REVERSE_START_PERCENT]: {
		strokeWidth: 133.5,
	},
	[ARC_REVERSE_END_PERCENT]: {
		strokeWidth: 0,
	},
	'100%': {
		strokeWidth: 0,
	},
});

// 4. Cross rotates forward then reverses
// Structure: hold 0deg -> animate to 90deg -> hold 90deg -> animate back to 0deg -> hold 0deg
const crossSpinKeyframes = keyframes({
	'0%': {
		transform: 'rotate(0deg)',
	},
	[CROSS_START_PERCENT]: {
		transform: 'rotate(0deg)',
	},
	[CROSS_END_PERCENT]: {
		transform: 'rotate(90deg)',
	},
	[CROSS_REVERSE_START_PERCENT]: {
		transform: 'rotate(90deg)',
	},
	[CROSS_REVERSE_END_PERCENT]: {
		transform: 'rotate(0deg)',
	},
	'100%': {
		transform: 'rotate(0deg)',
	},
});

/* eslint-enable @atlaskit/ui-styling-standard/no-unsafe-values */

// Reduced motion: simple fade in/out animation
const reducedMotionFadeKeyframes = keyframes({
	'0%': {
		opacity: 0.3,
	},
	'50%': {
		opacity: 1,
	},
	'100%': {
		opacity: 0.3,
	},
});

const containerStyles = css({
	display: 'flex',
	width: '100%',
	height: '100vh',
	position: 'fixed',
	zIndex: 100,
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: token('elevation.surface'),
	color: token('color.text'),
	cursor: 'pointer',
	insetBlockStart: 0,
	insetInlineStart: 0,
	opacity: 1,
	pointerEvents: 'auto',
	transition: `opacity 300ms ${token('motion.easing.out.bold')}, background-color ${token('motion.duration.medium')} ${token('motion.easing.out.bold')}, color ${token('motion.duration.medium')} ${token('motion.easing.out.bold')}`,
});

const containerFadingOutStyles = css({
	opacity: 0,
	pointerEvents: 'none',
});

const gridStyles = css({
	display: 'grid',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	columnGap: '11.545px', // Half of 23.09px
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	rowGap: '11.545px', // Half of 23.09px
	gridTemplateColumns: 'repeat(2, 74.75px)', // Half of 149.5px
	gridTemplateRows: 'repeat(2, 74.75px)', // Half of 149.5px
	'@media (prefers-reduced-motion: reduce)': {
		animation: `${reducedMotionFadeKeyframes} 2s ${token('motion.easing.inout.bold')} infinite`,
	},
});

const sparkleWrapperStyles = css({
	display: 'flex',
	width: '100%',
	height: '100%',
	alignItems: 'center',
	justifyContent: 'center',
	pointerEvents: 'auto',
	'& svg': {
		display: 'block',
		pointerEvents: 'none',
		transition: 'transform 0.3s ease-out',
	},
	'&:hover svg': {
		transform: 'scale(0.9)',
	},
	'& svg mask [data-sparkle-path]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		animation: `${sparkleGrowKeyframes} ${String(TOTAL_CYCLE_TIME)}s ease-out 0s infinite`,
		transformBox: 'fill-box',
		transformOrigin: '298.919px 299.081px', // Center of the path geometry
	},
	'@media (prefers-reduced-motion: reduce)': {
		'& svg mask [data-sparkle-path]': {
			animation: 'none',
			transform: 'scale(1)',
		},
	},
});

const squareWrapperStyles = css({
	display: 'flex',
	width: '100%',
	height: '100%',
	alignItems: 'center',
	justifyContent: 'center',
	pointerEvents: 'auto',
	'& svg': {
		display: 'block',
		pointerEvents: 'none',
		transition: 'transform 0.3s ease-out',
	},
	'&:hover svg': {
		transform: 'scale(0.9)',
	},
	'& [data-square-circle]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		animation: `${squareGrowKeyframes} ${String(TOTAL_CYCLE_TIME)}s ease-out 0s infinite`,
		transformOrigin: '298.906px 299.068px', // Center of SVG viewBox (597.812/2, 598.135/2)
		transition: 'transform 0.6s ease-out',
	},
	'@media (prefers-reduced-motion: reduce)': {
		'& [data-square-circle]': {
			animation: 'none',
			transform: 'scale(1)',
		},
	},
});

const arcWrapperStyles = css({
	display: 'flex',
	width: '100%',
	height: '100%',
	alignItems: 'center',
	justifyContent: 'center',
	pointerEvents: 'auto',
	'& svg': {
		display: 'block',
		pointerEvents: 'none',
		transition: 'transform 0.3s ease-out',
	},
	'&:hover svg': {
		transform: 'scale(0.9)',
	},
	'& [data-arc-stroke]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		animation: `${arcStrokeKeyframes} ${String(TOTAL_CYCLE_TIME)}s ease-out 0s infinite`,
		transition: 'stroke-width 0.6s ease-out',
	},
	'@media (prefers-reduced-motion: reduce)': {
		'& [data-arc-stroke]': {
			animation: 'none',
			strokeWidth: 133.5,
		},
	},
});

const crossWrapperStyles = css({
	display: 'flex',
	width: '100%',
	height: '100%',
	alignItems: 'center',
	justifyContent: 'center',
	pointerEvents: 'auto',
	'& svg': {
		display: 'block',
		pointerEvents: 'none',
		transition: 'transform 0.3s ease-out',
	},
	'&:hover svg': {
		transform: 'scale(0.9)',
	},
	'& [data-cross-shape]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		animation: `${crossSpinKeyframes} ${String(TOTAL_CYCLE_TIME)}s ease-out 0s infinite`,
		transformOrigin: '299px 299.5px', // Center of SVG (viewBox center: 598/2, 599/2)
		transition: 'transform 0.6s ease-in-out',
	},
	'@media (prefers-reduced-motion: reduce)': {
		'& [data-cross-shape]': {
			animation: 'none',
			transform: 'rotate(0deg)',
		},
	},
});

/**
 * __Landing page spinner__
 *
 * A landing page spinner using the Atlassian Design glyphs.
 *
 */
export const LandingPageSpinner = ({ isFadingOut }: { isFadingOut?: boolean }): JSX.Element => {
	return (
		<div css={[containerStyles, isFadingOut && containerFadingOutStyles]}>
			<div css={gridStyles}>
				<div css={sparkleWrapperStyles} id="sparkle-wrapper">
					<Sparkle />
				</div>
				<div css={squareWrapperStyles} data-square-wrapper>
					<Square />
				</div>
				<div css={arcWrapperStyles}>
					<Arc />
				</div>
				<div css={crossWrapperStyles}>
					<Cross />
				</div>
			</div>
		</div>
	);
};
